/**
 * W3 — MCP Control Center API client.
 *
 * Thin fetch wrapper matching the existing codebase convention (see
 * `src/lib/api/identity/identity-api.ts` /
 * `src/lib/api/notifications/notifications-api.ts`). RTK Query is intentionally
 * NOT used here — kept consistent with the other Phase 1+ clients until a
 * later phase unifies everything.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * Per CLAUDE.md, this token lives in Redux ONLY and is never persisted.
 *
 * Base URL: `CENTRAL_API_BASE_URL` from `src/lib/constants/central-api.ts`,
 * which already resolves to `${env-base}/api/v1/` across DEV/STAGING/LIVE —
 * so the relative paths below intentionally omit the `/api/v1/` prefix.
 *
 * Backend contract (PR #2024):
 *   GET    /api/v1/mcp-catalog
 *   GET    /api/v1/shops/{slug}/mcp-connections
 *   POST   /api/v1/shops/{slug}/mcp-connections
 *   POST   /api/v1/shops/{slug}/mcp-connections/{id}/reintrospect
 *   POST   /api/v1/shops/{slug}/mcp-connections/{id}/oauth/authorize
 *   PATCH  /api/v1/shops/{slug}/mcp-connections/{id}/tools/{tool}
 *   DELETE /api/v1/shops/{slug}/mcp-connections/{id}
 *
 * All list/object responses may arrive either bare or `{ data: ... }`-wrapped;
 * `unwrap()` / the array guards below normalise both shapes.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/** A vetted catalog entry the client can one-click connect. */
export interface McpCatalogEntry {
  key: string;
  name: string;
  auth_type: string;
  oauth_supported: boolean;
  description: string;
  /**
   * Optional brand-logo URL for the catalog card. Added to `/mcp-catalog` in
   * parallel; when present the catalog card renders it like the legacy
   * Integrations logo cards, otherwise it falls back to the generic chip icon.
   */
  logo?: string;
}

export type ToolClassification = 'read' | 'write';
export type ToolPolicy = 'allow' | 'require_approval' | 'block';
export type McpConnectionStatus = 'connected' | 'error' | 'pending' | string;
export type McpConnectionType = 'catalog' | 'custom';
/**
 * - `none` / `bearer`: a custom server with no auth or a static bearer secret.
 * - `oauth`: the brand's stored PLATFORM token (catalog connections only). The
 *   UI never offers it for custom servers.
 * - `mcp_oauth`: a custom server that requires OAuth per the MCP authorization
 *   spec. central-api runs discovery, client registration and PKCE; the user
 *   only signs in at the provider.
 */
export type McpAuthType = 'none' | 'bearer' | 'oauth' | 'mcp_oauth';

/** Authorization state of an `mcp_oauth` connection. */
export type McpOauthStatus =
  | 'authorized'
  | 'authorization_required'
  | 'reconnect_required';

/**
 * The `oauth` block the list endpoint adds to every `mcp_oauth` row. It never
 * carries a token or client secret.
 */
export interface McpConnectionOauth {
  status: McpOauthStatus | string;
  authorization_server_host?: string | null;
  scopes?: string | string[] | null;
  authorized_at?: string | null;
  /** Display name of the employee who authorized the connection. */
  authorized_by?: string | null;
  expires_at?: string | null;
  client_registration?: 'dynamic' | 'manual' | string | null;
}

/** What the browser needs to start (or restart) an OAuth authorization. */
export interface McpOauthAuthorization {
  authorize_url: string;
  authorization_server_host?: string | null;
}

/** A single introspected tool exposed by an MCP server. */
export interface McpTool {
  tool_name: string;
  classification: ToolClassification;
  policy: ToolPolicy;
  description: string;
}

/** A configured MCP server connection scoped to a shop. */
export interface McpConnection {
  id: number | string;
  name: string;
  type: McpConnectionType;
  platform_key: string | null;
  url: string;
  auth_type: McpAuthType;
  status: McpConnectionStatus;
  last_introspected_at: string | null;
  tools: McpTool[];
  // Present when `status === 'error'` (introspection failed).
  last_error?: string | null;
  // Additive credential signals (mirrors the Platforms tab). Optional so older
  // payloads still parse; when absent the governance pill falls back to
  // `status`. `credential_present` is a non-nullable bool in the contract; the
  // rest are `bool|null`.
  //   - credential_present: a credential row exists at all.
  //   - credential_valid:   the credential is accepted (not rejected/expired).
  //   - credential_active:  the on/off switch is on.
  //   - oauth_required:     the OAuth grant is missing or needs re-consent.
  credential_present?: boolean;
  credential_valid?: boolean | null;
  credential_active?: boolean | null;
  oauth_required?: boolean | null;
  // Present on `auth_type: 'mcp_oauth'` rows only.
  oauth?: McpConnectionOauth | null;
}

/**
 * The create response. For `mcp_oauth` the row is saved `pending` with no
 * tools, and `oauth` carries the URL the user must be sent to.
 */
export type CreateMcpConnectionResponse = Omit<McpConnection, 'oauth'> & {
  oauth?: (Partial<McpConnectionOauth> & Partial<McpOauthAuthorization>) | null;
};

/** Body for POST /mcp-connections. */
export interface CreateMcpConnectionRequest {
  name: string;
  type: McpConnectionType;
  url: string;
  auth_type: McpAuthType;
  auth_secret?: string;
  platform_key?: string;
  // `mcp_oauth` only, all optional. Leave empty to let central-api register a
  // client automatically (dynamic client registration).
  oauth_client_id?: string;
  oauth_client_secret?: string;
  /** Space-separated scopes. */
  oauth_scopes?: string;
}

/**
 * The error every call in this module throws on a non-2xx response.
 *
 * - `status`:       the HTTP status.
 * - `validation`:   Laravel's per-field `errors`, when present.
 * - `code`:         the machine `error` code of the control-center endpoints
 *                   (e.g. `client_registration_required`, `invalid_url`).
 * - `redirect_uri`: sent with `client_registration_required` — the callback
 *                   URL to register at the provider.
 *
 * `message` is the human `reason` (or Laravel's `message`) when there is one.
 */
export type ControlCenterApiError = Error & {
  status?: number;
  validation?: Record<string, string[]>;
  code?: string;
  redirect_uri?: string;
};

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[control-center-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    let validation: Record<string, string[]> | undefined;
    let code: string | undefined;
    let redirectUri: string | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        reason?: string;
        error?: string;
        errors?: Record<string, string[]>;
        redirect_uri?: string;
      }>(response);
      // Prefer the human `reason` the control-center endpoints send alongside
      // their machine `error` code — surfacing the bare code (e.g.
      // "duplicate_name") in the dialog reads as a bug.
      detail = body?.message || body?.reason || body?.error || '';
      validation = body?.errors;
      if (typeof body?.error === 'string') code = body.error;
      if (typeof body?.redirect_uri === 'string') {
        redirectUri = body.redirect_uri;
      }
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as ControlCenterApiError;
    err.status = response.status;
    if (validation) err.validation = validation;
    if (code) err.code = code;
    if (redirectUri) err.redirect_uri = redirectUri;
    throw err;
  }

  return parseJson<T>(response);
}

/** Unwrap a `{ data }` envelope when present; pass through a bare payload. */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function shopPath(slug: string): string {
  return `shops/${encodeURIComponent(slug)}/mcp-connections`;
}

// ---------- public API ----------

/** GET /api/v1/mcp-catalog — the vetted catalog of one-click MCP servers. */
export async function getMcpCatalog(token?: string): Promise<McpCatalogEntry[]> {
  const raw = await request<McpCatalogEntry[] | { data?: McpCatalogEntry[] }>(
    'mcp-catalog',
    { method: 'GET' },
    token
  );
  if (Array.isArray(raw)) return raw;
  return unwrap<McpCatalogEntry[]>(raw) ?? [];
}

/** GET /api/v1/shops/{slug}/mcp-connections — connected servers + their tools. */
export async function listMcpConnections(
  slug: string,
  token?: string
): Promise<McpConnection[]> {
  const raw = await request<McpConnection[] | { data?: McpConnection[] }>(
    shopPath(slug),
    { method: 'GET' },
    token
  );
  if (Array.isArray(raw)) return raw;
  return unwrap<McpConnection[]>(raw) ?? [];
}

/**
 * POST /api/v1/shops/{slug}/mcp-connections — add a catalog or custom server.
 *
 * Returns the connection with its introspected tools. `status` may be `'error'`
 * if introspection failed — in that case `last_error` carries the reason.
 *
 * For `auth_type: 'mcp_oauth'` nothing is introspected yet: the row comes back
 * `pending` and `oauth.authorize_url` is where the user must sign in. The
 * `oauth` block is read from the connection itself or, should it arrive next
 * to the `data` envelope, from the top level of the response.
 */
export async function createMcpConnection(
  slug: string,
  body: CreateMcpConnectionRequest,
  token?: string
): Promise<CreateMcpConnectionResponse> {
  const raw = await request<
    | CreateMcpConnectionResponse
    | {
        data?: CreateMcpConnectionResponse;
        oauth?: CreateMcpConnectionResponse['oauth'];
      }
  >(
    shopPath(slug),
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  const conn = unwrap<CreateMcpConnectionResponse>(
    raw as CreateMcpConnectionResponse
  );
  const envelopeOauth =
    raw && typeof raw === 'object' && 'data' in raw
      ? (raw as { oauth?: CreateMcpConnectionResponse['oauth'] }).oauth
      : undefined;
  if (conn && typeof conn === 'object' && !conn.oauth && envelopeOauth) {
    return { ...conn, oauth: envelopeOauth };
  }
  return conn;
}

/**
 * POST /api/v1/shops/{slug}/mcp-connections/{id}/oauth/authorize — a fresh
 * authorize URL for an `mcp_oauth` connection (first connect after a cancelled
 * redirect, or reconnect after the token died). The row, its tool policies and
 * exposed names stay as they are.
 */
export async function authorizeMcpConnection(
  slug: string,
  id: number | string,
  token?: string
): Promise<McpOauthAuthorization> {
  const raw = await request<
    McpOauthAuthorization | { data?: McpOauthAuthorization }
  >(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}/oauth/authorize`,
    { method: 'POST' },
    token
  );
  return unwrap<McpOauthAuthorization>(raw);
}

/** POST /api/v1/shops/{slug}/mcp-connections/{id}/reintrospect — re-scan tools. */
export async function reintrospectMcpConnection(
  slug: string,
  id: number | string,
  token?: string
): Promise<McpConnection> {
  const raw = await request<McpConnection | { data?: McpConnection }>(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}/reintrospect`,
    { method: 'POST' },
    token
  );
  return unwrap<McpConnection>(raw);
}

/**
 * PATCH /api/v1/shops/{slug}/mcp-connections/{id}/tools/{tool} — set one write
 * tool's policy. READ tools are always `'allow'` and not editable.
 */
export async function setToolPolicy(
  slug: string,
  id: number | string,
  toolName: string,
  policy: ToolPolicy,
  token?: string
): Promise<McpTool> {
  const raw = await request<McpTool | { data?: McpTool }>(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}/tools/${encodeURIComponent(
      toolName
    )}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ policy }),
    },
    token
  );
  return unwrap<McpTool>(raw);
}

/** DELETE /api/v1/shops/{slug}/mcp-connections/{id} — remove a connection. */
export async function deleteMcpConnection(
  slug: string,
  id: number | string,
  token?: string
): Promise<void> {
  await request<unknown>(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}`,
    { method: 'DELETE' },
    token
  );
}
